import { useState, useEffect, type Key } from "react";
import AntDTable from "@/common/components/ui/antd-table";
import { Card, CardContent } from "@/common/components/ui/card";
import { Badge } from "@/common/components/ui/badge";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Separator } from "@/common/components/ui/separator";
import LogsService from "@/services/FinEaseHomeAndAdmin/APIs/LogsService";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
    AlertCircle,
    Search,
    RefreshCw,
    Download,
    Filter,
    ServerCrash,
    Eye,
    Globe,
    Laptop,
    FileText
} from "lucide-react";
import CommonModal from "@/common/components/ui/modal";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";

const ApiIssueLogs = () => {
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();

    // Data States
    const [apiLogs, setApiLogs] = useState<any[]>([]);
    const [selectedLog, setSelectedLog] = useState<any | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    // Pagination States
    const [apiPagination, setApiPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const fetchLogs = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const res: any = await LogsService.getApiIssueLogsAPI(page, pageSize);
            const data = res.results ? res : res.data;
            if (data?.results) {
                setApiLogs(data.results);
                setApiPagination({ current: data.page, pageSize: data.page_size, total: data.count });
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error fetching logs",
                description: error.message ?? "Something went wrong",
            });
        } finally {
            setLoading(false);
        }
    };

    // Initial Fetch
    useEffect(() => {
        fetchLogs(apiPagination.current, apiPagination.pageSize);
    }, []);

    const handleRefresh = () => {
        fetchLogs(apiPagination.current, apiPagination.pageSize);
        toast({ title: "Refreshed", description: "API logs updated." });
    };

    const handlePageChange = (page: number, pageSize: number) => {
        fetchLogs(page, pageSize);
    };

    const formatDate = (dateUnparsed: string) => {
        try {
            return format(new Date(dateUnparsed), "MMM dd, yyyy HH:mm:ss");
        } catch (_) {
            return dateUnparsed;
        }
    };

    const getStatusColor = (code: number) => {
        if (code >= 500) { return "bg-red-100 text-red-700 border-red-200"; }
        if (code >= 400) { return "bg-amber-100 text-amber-700 border-amber-200"; }
        if (code >= 200 && code < 300) { return "bg-green-100 text-green-700 border-green-200"; }
        return "bg-gray-100 text-gray-700 border-gray-200";
    };

    const onSelectChange = (keys: Key[], rows: any[]) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
    };

    const handleExport = () => {
        if (selectedRows.length === 0) {
            notify.error("No Data", "Please select items to export.");
            return;
        }

        const rows = selectedRows.map(item => ({
            Timestamp: item.created_on ? format(new Date(item.created_on), "MMM dd, yyyy HH:mm:ss") : "-",
            Method: item.request_method ?? "-",
            Path: item.request_path ?? "-",
            Status: item.status_code ?? "-",
            Exception: item.exception_type ?? "-",
            User: item.user_email ?? "Anonymous",
            IP: item.ip_address ?? "-",
            Message: item.error_message ?? "-",
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, "ApiIssueLogs");

        XLSX.writeFile(
            wb,
            `api_issue_logs_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        notify.success("Exported", `${rows.length} record(s) exported.`);
    };

    // --- Columns Definitions ---

    const apiColumns = [
        {
            title: "Timestamp",
            dataIndex: "created_on",
            key: "created_on",
            width: 250,
            render: (text: string) => (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-700 text-sm">{formatDate(text)}</span>
                    <span className="text-xs text-muted-foreground">{format(new Date(text), "PP")}</span>
                </div>
            ),
        },
        {
            title: "Request Context",
            key: "request",
            width: 250,
            render: (_: any, record: any) => (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-[10px] font-mono border-gray-200">
                            {record.request_method}
                        </Badge>
                        <span className="text-xs font-mono text-gray-700 truncate max-w-[180px]" title={record.request_path}>
                            {record.request_path}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] px-1.5 py-0.5 border shadow-sm ${getStatusColor(record.status_code)}`}>
                            {record.status_code}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground font-medium">
                            {record.exception_type ?? "N/A"}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            title: "User Identity",
            key: "user",
            width: 250,
            render: (_: any, record: any) => (
                <div className="flex flex-col gap-1">
                    <span className="font-medium text-gray-900 text-sm">
                        {record.user_email ?? "Anonymous User"}
                    </span>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1 bg-gray-100 px-1.5 py-0.5 rounded">
                            <Globe className="w-3 h-3" /> {record.ip_address}
                        </span>
                    </div>
                </div>
            )
        },
        {
            title: "Error Message",
            dataIndex: "error_message",
            key: "error_message",
            width: 250,
            render: (text: string) => (
                <div className="flex items-start gap-2 max-w-[250px]">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <span className="text-xs text-red-600 line-clamp-2" title={text}>{text}</span>
                </div>
            ),
        },
        {
            title: "Details",
            key: "action",
            width: 80,
            align: "right" as const,
            render: (_: any, record: any) => (
                <Button
                    variant="ghost"
                    size="sm"
                    className="hover:bg-red-50 hover:text-red-600"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLog(record);
                    }}
                >
                    <Eye className="w-4 h-4 mr-1.5" />
                    View
                </Button>
            )
        }
    ];

    return (
        <>
            <div className="min-h-screen bg-gray-50/50 p-6 space-y-8 font-sans">
                {/* Header Section */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
                    <div className="absolute right-0 top-0 opacity-10">
                        <ServerCrash className="h-48 w-48 -translate-y-8 translate-x-12 transform text-white" />
                    </div>
                    <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
                        <div className="flex items-center gap-4">
                            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                                <AlertCircle className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <h1 className="font-display text-2xl font-bold text-white">
                                    API Issue Logs
                                </h1>
                                <p className="text-white/70">Monitor API failures, exceptions, and critical system errors.</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Button
                                variant="outline"
                                className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white transition-all backdrop-blur-md"
                                onClick={handleRefresh}
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Sync Data
                            </Button>
                            <Button
                                variant="outline"
                                className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
                                onClick={handleExport}
                                disabled={selectedRows.length === 0}
                            >
                                <Download className="h-4 w-4" />
                                Export
                                {selectedRows.length > 0 && (
                                    <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                                        {selectedRows.length}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid gap-6 md:grid-cols-1">
                    <Card className="overflow-hidden border-none shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <p className="text-sm font-medium text-muted-foreground">API Error Count</p>
                                    <div className="flex items-baseline gap-2">
                                        <h2 className="text-3xl font-bold text-gray-900">{apiPagination.total}</h2>
                                        <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-0.5 rounded-full">Critical</span>
                                    </div>
                                </div>
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                                    <ServerCrash className="h-6 w-6 text-red-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content Area */}
                {/* Toolbar & Filters */}
                <div className="border-b p-5 bg-white">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="relative max-w-sm w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search error logs..."
                                className="pl-9 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <Button variant="outline" size="icon" className="h-10 w-10 text-muted-foreground">
                                <Filter className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Table Content */}
                <div className="p-4 bg-gray-50/40 border-b flex justify-between items-center text-xs text-muted-foreground">
                    <span>Showing {apiLogs.length} recent error logs</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Live</span>
                </div>
                <AntDTable
                    columns={apiColumns}
                    data={apiLogs}
                    loading={loading}
                    showSettings
                    pagination={{
                        current: apiPagination.current,
                        pageSize: apiPagination.pageSize,
                        total: apiPagination.total,
                        onChange: handlePageChange,
                        showSizeChanger: true
                    }}
                    rowKey="id"
                    rowSelection={{
                        selectedRowKeys,
                        onChange: onSelectChange,
                    }}
                    className="border-0"
                    onRow={(record) => ({
                        onClick: () => setSelectedLog(record),
                        className: "cursor-pointer hover:bg-gray-50 transition-colors"
                    })}
                />
            </div>

            <CommonModal
                isOpen={!!selectedLog}
                onClose={() => setSelectedLog(null)}
                width="max-w-4xl w-full"
                height="h-auto"
                padding="p-0"
            >
                <div className="flex flex-col">
                    <div className="border-b rounded-t-full p-6 pb-4">
                        <div className="flex items-center gap-4 mb-2">
                            <Badge
                                className={`uppercase text-xs font-bold px-2 py-1 ${getStatusColor(selectedLog?.status_code)}`}
                            >
                                Status: {selectedLog?.status_code}
                            </Badge>
                            <h2 className="text-xl font-semibold text-gray-900">
                                {selectedLog?.error_code ?? "Unknown Error"}
                            </h2>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs font-mono text-muted-foreground mt-2">
                            <span className="bg-white px-2 py-1 border rounded shadow-sm text-gray-700">
                                {selectedLog?.request_method}
                            </span>
                            <span className="truncate max-w-md">
                                {selectedLog?.request_path}
                            </span>
                            <span className="text-gray-400">|</span>
                            <span>ID: {selectedLog?.id}</span>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="space-y-8">
                            {/* Detailed Info Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Globe className="w-4 h-4 text-blue-500" /> User Context
                                    </h4>
                                    <div className="grid grid-cols-[100px_1fr] gap-3">
                                        <span className="text-muted-foreground">User:</span>
                                        <span className="font-medium text-gray-900 border-b border-dashed w-fit pb-0.5">
                                            {selectedLog?.user_email ?? "N/A"}
                                        </span>

                                        <span className="text-muted-foreground">Tenant:</span>
                                        <span>{selectedLog?.tenant_name ?? "N/A"}</span>

                                        <span className="text-muted-foreground">IP Address:</span>
                                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded w-fit text-gray-700">
                                            {selectedLog?.ip_address}
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Laptop className="w-4 h-4 text-purple-500" /> System Details
                                    </h4>
                                    <div className="grid grid-cols-[100px_1fr] gap-3">
                                        <span className="text-muted-foreground">Exception:</span>
                                        <span className="font-mono text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded w-fit">
                                            {selectedLog?.exception_type ?? "N/A"}
                                        </span>

                                        <span className="text-muted-foreground">Time:</span>
                                        <span>{selectedLog && formatDate(selectedLog.created_on)}</span>

                                        <span className="text-muted-foreground">User Agent:</span>
                                        <span className="text-xs text-muted-foreground break-all leading-tight">
                                            {selectedLog?.user_agent}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* Error Message & Technical Details */}
                            <div className="space-y-4">
                                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-orange-500" /> Technical Details
                                </h4>

                                {/* Error Message */}
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Error Message</div>
                                    <div className="bg-red-50/50 border border-red-100 rounded-lg p-3 text-sm text-red-700">
                                        {selectedLog?.error_message}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Response Preview */}
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex justify-between">
                                            <span>Response Preview</span>
                                            <Badge variant="outline" className="text-[10px] h-5">JSON</Badge>
                                        </div>
                                        <div className="bg-gray-900 rounded-lg p-4 overflow-auto max-h-[300px]">
                                            <pre className="text-xs font-mono text-green-400 whitespace-pre-wrap break-all">
                                                {selectedLog?.response_preview ?? "No response preview available"}
                                            </pre>
                                        </div>
                                    </div>

                                    {/* Request Body (if available) - Placeholder for structure */}
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex justify-between">
                                            <span>Request Details</span>
                                        </div>
                                        <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 font-mono text-xs text-gray-600 space-y-2">
                                            <div className="flex gap-2">
                                                <span className="font-bold text-gray-900">Method:</span>
                                                <span>{selectedLog?.request_method}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="font-bold text-gray-900">Path:</span>
                                                <span className="break-all">{selectedLog?.request_path}</span>
                                            </div>
                                            <div className="border-t pt-2 mt-2">
                                                <span className="block font-bold text-gray-900 mb-1">Raw Error:</span>
                                                <span className="break-all">{JSON.stringify(selectedLog?.error_code, null, 2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CommonModal>
        </>
    );
};

export default ApiIssueLogs;
